import type { ReactNode } from 'react';
import { createContext, use, useMemo, useState } from 'react';
import { PreferencesDialog } from './PreferencesDialog';

export interface PreferencesDialogApi {
  /** Opens the shared Preferences dialog. */
  open: () => void;
}

const PreferencesDialogContext = createContext<PreferencesDialogApi | null>(null);

/**
 * Provides one shared Preferences dialog for the authenticated shell, opened
 * from two places: the TopBar user menu and the My-profile header action. The
 * dialog is mounted once here (like `ToastProvider`) and reads `GET /me` only
 * while open, so there's a single source of truth and no duplicate dialogs.
 */
export function PreferencesDialogProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const api = useMemo<PreferencesDialogApi>(() => ({ open: () => setOpen(true) }), []);

  return (
    <PreferencesDialogContext value={api}>
      {children}
      <PreferencesDialog open={open} onClose={() => setOpen(false)} />
    </PreferencesDialogContext>
  );
}

/**
 * Access the shared Preferences dialog opener. Returns `null` when rendered
 * outside a {@link PreferencesDialogProvider} — callers (e.g. the TopBar unit
 * test harness) then fall back to navigating to `/profile`.
 */
export function usePreferencesDialog(): PreferencesDialogApi | null {
  return use(PreferencesDialogContext);
}
